<template>
  <article v-if="getDeviceType() !== 'Manual Entry'" class="panel is-danger">
    <WtTitle class="panel-heading" size="h3">
      Device Diagnostics (Super Admin Only)
    </WtTitle>

    <WtTile :col="12" :is-loading="isProcessingRequest" child>
      <WtTitle size="h3">Device Information</WtTitle>

      <WtTitle size="h5">Device Type:</WtTitle>

      <p>{{ getDeviceType() }}</p>
      <br />

      <WtTitle size="h5">Connected At:</WtTitle>

      <p>
        {{ date(connectionDate, "DATE_TIME_FORMAT").formatted }}
      </p>
      <br />

      <WtTitle size="h5">Last Synced At:</WtTitle>

      <p>
        {{ date(deviceInfo.lastSyncedAt, "DATE_TIME_FORMAT").formatted }}
      </p>
      <br />

      <WtButton
        color="red"
        size="small"
        :loading="isProcessingRequest"
        :disabled="isProcessingRequest"
        bold
        @click.native="disconnectDevice"
      >
        DISCONNECT DEVICE
      </WtButton>
    </WtTile>

    <WtTile :col="12" :is-loading="isProcessingRequest" child>
      <WtTitle size="h3">Last seven days of data</WtTitle>

      <div v-if="syncData.length > 0">
        <div v-for="(r, i) in syncData" :key="i">
          <WtTitle size="h5">
            {{ date(r.data_date, "DATE_FORMAT_FOR_DATE_OBJ").formatted }}
            (updated {{ formatWithCommas(r.data_update_count) }} times.)
          </WtTitle>

          <p>Total Steps: {{ formatWithCommas(r.data_steps) }}</p>
          <p>
            Recorded On:
            {{ date(r.data_updatedate, "DATE_FORMAT_FOR_DATE_OBJ").formatted }}
          </p>
          <p>Data Source: {{ r.data_source }}</p>
          <br v-if="i - 1 < syncData.length" />
        </div>
      </div>
      <div v-else>
        <WtTitle size="h5">
          No data have been logged the last seven days.
        </WtTitle>
      </div>
    </WtTile>

    <WtTile
      v-if="isMobileHealth"
      :col="12"
      :is-loading="isProcessingRequest"
      child
    >
      <WtTitle size="h3">Connection Date</WtTitle>

      <p class="has-text-grey">
        Changing the connection date will provide the member a way to be able to
        pull in steps before their original connection date.
        <br /><br />
        However, we cannot force these steps to come in, but must rely on the
        member.
      </p>

      <hr />

      <WtTile :col="4" child>
        <BField>Connection Date</BField>
        <BDatepicker
          v-model="newConnectionDateValue"
          v-validate="'required'"
          data-vv-name="connectionDate"
          placeholder="Connection Date"
          icon="calendar-today"
          indicators="bars"
          :date-formatter="dt => date(dt, 'DATE_FORMAT_FOR_DATE_OBJ').formatted"
          :editable="true"
          :max-date="today.dateObject"
        ></BDatepicker>
        <br />
        <WtButton
          size="small"
          :loading="isProcessingRequest"
          :disabled="isProcessingRequest"
          bold
          @click.native="updateConnectionDate"
        >
          Change connection Date
        </WtButton>
      </WtTile>
    </WtTile>
  </article>
</template>

<script>
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle.vue";
import WtButton from "@@/WtButton/WtButton.vue";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";

export default {
  name: "AdminSuperDeviceDiagnostics",

  components: { WtButton, WtTile, WtTitle },
  mixins: [dateUtilityMixin, numberUtilityMixin],
  props: {
    member: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isProcessingRequest: true,
      newConnectionDateValue: this.today().dateObject,
      connectionDateValue: this.today().dateObject,
      deviceInfo: [],
      syncData: []
    };
  },
  computed: {
    connectionDate: {
      set(connectionDateValue) {
        this.connectionDateValue = connectionDateValue;
      },
      get() {
        return this.connectionDateValue;
      }
    }
  },

  mounted() {
    this.getDeviceInfo();
  },

  methods: {
    async getDeviceInfo() {
      await this.axios
        .get(`super/device/${this.member.id}`)
        .then(response => {
          this.isProcessingRequest = false;

          if (response.data.success === true) {
            let resp = response.data.data;

            this.deviceInfo = resp.device;
            this.syncData = resp.syncData;
            this.connectionDate = this.date(resp.device.connectedAt).dateObject;
            this.newConnectionDateValue = this.date(
              resp.device.connectedAt
            ).dateObject;
          } else {
            this.$buefy.toast.open("Failed to load device information.");
          }
        })
        .catch(error => {
          this.$buefy.toast.open("Failed to load device information.");

          console.log("error: ", error);
        });
    },
    async disconnectDevice() {
      this.$buefy.dialog.confirm({
        title: "Disconnect Device",
        confirmText: "Disconnect",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want to disconnect this member's device?",
        onConfirm: async () => {
          await this.disconnectMembersDevice();
        }
      });
    },
    async disconnectMembersDevice() {
      this.isProcessingRequest = true;

      await this.axios
        .delete(`super/device/${this.member.id}`)
        .then(response => {
          this.isProcessingRequest = false;

          if (response.data.success === true) {
            this.$buefy.toast.open("Device disconnected successfully.");

            this.getDeviceInfo();
          } else {
            this.$buefy.toast.open("Failed to disconnect device.");
          }
        })
        .catch(error => {
          this.isProcessingRequest = false;
          this.$buefy.toast.open("Failed to disconnect device.");

          console.log("error: ", error);
        });
    },
    async updateConnectionDate() {
      this.isProcessingRequest = true;

      let request = {
        memberId: this.member.id,
        connectionDate: this.date(this.newConnectionDateValue, "DATE_TIME_FULL")
          .formatted
      };

      await this.axios
        .post("super/device/update-connection-date", request)
        .then(response => {
          this.isProcessingRequest = false;

          if (response.data.success === true) {
            this.$buefy.toast.open("Updated connection date successfully.");

            this.getDeviceInfo();
          } else {
            this.$buefy.toast.open("Failed to update connection date.");
          }
        })
        .catch(error => {
          this.isProcessingRequest = false;
          this.$buefy.toast.open("Failed to update connection date.");

          console.log("error: ", error);
        });
    },
    getDeviceType() {
      if (this.deviceInfo.deviceType === "googlefit") {
        return "Google Fit";
      } else if (this.deviceInfo.deviceType === "healthkit") {
        return "Apple HealthKit";
      } else {
        return this.deviceInfo.deviceType;
      }
    },
    isMobileHealth() {
      return (
        this.deviceInfo.deviceType === "googlefit" ||
        this.deviceInfo.deviceType === "healthkit"
      );
    }
  }
};
</script>
